import { createFileRoute, Link, notFound, redirect } from "@tanstack/react-router";
import { DistrictHubPage, districtQuery } from "@/components/districts/DistrictHubPage";

const CANONICAL_REDIRECTS: Record<string, "/train-factory"> = {
  "train-factory": "/train-factory",
  konepaja: "/train-factory",
};

export const Route = createFileRoute("/districts/$slug")({
  beforeLoad: ({ params }) => {
    const to = CANONICAL_REDIRECTS[params.slug];
    if (to) throw redirect({ to });
  },
  loader: async ({ params, context }) => {
    const hub = await context.queryClient.ensureQueryData(districtQuery(params.slug));
    if (!hub) throw notFound();
    return { name: hub.district.name, description: hub.district.description, hero: hub.district.hero_image_url };
  },
  head: ({ params, loaderData }) => {
    if (!loaderData) {
      return { meta: [{ title: "District not found — what Finland" }, { name: "robots", content: "noindex" }] };
    }
    const url = `https://whatfinland.com/districts/${params.slug}`;
    const title = `${loaderData.name} — Restaurants, Hours & Menus | what Finland`;
    const desc =
      (loaderData.description ?? "").slice(0, 155) ||
      `See every restaurant and bar in ${loaderData.name}, which ones are open right now, and today's menus.`;
    return {
      meta: [
        { title },
        { name: "description", content: desc },
        { property: "og:title", content: title },
        { property: "og:description", content: desc },
        { property: "og:type", content: "website" },
        { property: "og:url", content: url },
        { name: "twitter:card", content: "summary_large_image" },
        ...(loaderData.hero && loaderData.hero.startsWith("https://")
          ? [
              { property: "og:image", content: loaderData.hero },
              { name: "twitter:image", content: loaderData.hero },
            ]
          : []),
      ],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: "https://whatfinland.com/" },
              { "@type": "ListItem", position: 2, name: "Districts", item: "https://whatfinland.com/districts" },
              { "@type": "ListItem", position: 3, name: loaderData.name, item: url },
            ],
          }),
        },
      ],
    };
  },
  component: DistrictSlugPage,
  notFoundComponent: () => (
    <div className="flex min-h-screen items-center justify-center p-8 text-center">
      <div>
        <h1 className="font-heading text-2xl">District not available</h1>
        <p className="mt-2 text-slate-500">This district hub hasn’t been published yet.</p>
        <Link to="/districts" className="mt-6 inline-block text-royal underline">
          Browse all districts
        </Link>
      </div>
    </div>
  ),
});

function DistrictSlugPage() {
  const { slug } = Route.useParams();
  return <DistrictHubPage slug={slug} />;
}
